/**
 * PlaudTranscriptModal — Full transcript viewer with timestamps and speakers.
 *
 * Features:
 * - Timestamped segments with speaker labels
 * - Speaker color coding for visual distinction
 * - Search within transcript with highlighting
 * - Key topics and action items extraction
 * - Jump to timestamp (scroll to segment)
 * - Link contacts to recording
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, FileText, Search, Clock, User, Loader2, AlertCircle, Copy, Check, Users, CheckSquare, Tag, ChevronDown, ChevronUp, FileDown } from 'lucide-react';
import apiClient from '../../lib/apiClient';

// Speaker color palette - uses theme variables where possible
const SPEAKER_COLORS = [
  'var(--color-primary)',      // Blue
  'var(--color-success)',      // Green
  '#f97316',                   // Orange
  '#8b5cf6',                   // Purple
  '#06b6d4',                   // Cyan
  '#ec4899',                   // Pink
  'var(--color-warning)',      // Yellow/Amber
  '#14b8a6',                   // Teal
];

// Escape special regex characters
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Highlight matched text in content
function highlightMatch(text, query) {
  if (!query || !text) return text;
  const escaped = escapeRegex(query);
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (regex.test(part)) {
      return <mark key={i} className="aw-transcript-highlight">{part}</mark>;
    }
    return part;
  });
}

// Parse action items and topics from note_raw
function parseNoteContent(noteRaw) {
  if (!noteRaw) return { actionItems: [], topics: [], summary: null };

  const result = { actionItems: [], topics: [], summary: null };

  try {
    // Handle different note formats
    let noteContent = null;

    if (typeof noteRaw === 'string') {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(noteRaw);
        noteContent = parsed;
      } catch {
        // It's plain text
        noteContent = noteRaw;
      }
    } else if (Array.isArray(noteRaw)) {
      // Look for auto_sum_note type
      const autoSum = noteRaw.find(n => n.data_type === 'auto_sum_note');
      if (autoSum?.data_content) {
        noteContent = autoSum.data_content;
      }
    } else {
      noteContent = noteRaw.data_content || noteRaw.content || noteRaw;
    }

    if (typeof noteContent === 'string') {
      // Extract action items (lines starting with - [ ], *, •, or numbered)
      const actionRegex = /^[\s]*(?:[-*•]|\d+\.)\s*(?:\[[ x]\])?\s*(.+)$/gm;
      let match;
      while ((match = actionRegex.exec(noteContent)) !== null) {
        const item = match[1].trim();
        if (item.toLowerCase().includes('action') ||
            item.toLowerCase().includes('follow') ||
            item.toLowerCase().includes('next step') ||
            item.toLowerCase().includes('todo') ||
            item.toLowerCase().includes('need to') ||
            item.toLowerCase().includes('will ')) {
          result.actionItems.push(item);
        }
      }

      // Extract topics (look for headers or key phrases)
      const topicRegex = /(?:^#+\s*|key (?:topics?|points?|takeaways?):?\s*|discussed:?\s*)(.+)$/gim;
      while ((match = topicRegex.exec(noteContent)) !== null) {
        const topic = match[1].trim().replace(/^[:#\s]+/, '');
        if (topic.length > 2 && topic.length < 100) {
          result.topics.push(topic);
        }
      }

      // If no structured topics found, try to extract from bullet points
      if (result.topics.length === 0) {
        const bulletRegex = /^[\s]*[-*•]\s+([A-Z][^.!?]+[.!?]?)$/gm;
        let bulletMatch;
        let bulletCount = 0;
        while ((bulletMatch = bulletRegex.exec(noteContent)) !== null && bulletCount < 5) {
          const topic = bulletMatch[1].trim();
          if (topic.length > 10 && topic.length < 100 && !result.actionItems.includes(topic)) {
            result.topics.push(topic);
            bulletCount++;
          }
        }
      }
    }
  } catch (e) {
    console.warn('Failed to parse note content:', e);
  }

  return result;
}

function fmtTimestamp(ms) {
  if (!ms && ms !== 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PlaudTranscriptModal({ recording, onClose, contacts = [], onContactsLinked }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedForDocs, setCopiedForDocs] = useState(false);
  const [showTopics, setShowTopics] = useState(true);
  const [showLinkContacts, setShowLinkContacts] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(recording.linked_contact_ids || []);
  const segmentsRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['plaud-transcript', recording.id],
    queryFn: () => apiClient.get(`/api/plaud/recordings/${recording.id}/transcript`),
    enabled: !!recording.id,
  });

  // Get full recording details to access note_raw
  const { data: fullRecording } = useQuery({
    queryKey: ['plaud-recording', recording.id],
    queryFn: () => apiClient.get(`/api/plaud/recordings/${recording.id}`),
    enabled: !!recording.id,
  });

  const segments = data?.segments || [];

  // Build speaker → color mapping based on first appearance
  const speakerColorMap = useMemo(() => {
    const map = new Map();
    segments.forEach((seg) => {
      const speaker = seg.speaker || 'Speaker';
      if (!map.has(speaker)) {
        map.set(speaker, SPEAKER_COLORS[map.size % SPEAKER_COLORS.length]);
      }
    });
    return map;
  }, [segments]);

  // Parse topics and action items from notes
  const { actionItems, topics } = useMemo(() => {
    return parseNoteContent(fullRecording?.note_raw);
  }, [fullRecording?.note_raw]);

  // Filter segments by search query
  const filteredSegments = useMemo(() => {
    if (!searchQuery) return segments;
    const query = searchQuery.toLowerCase();
    return segments.filter(seg =>
      seg.content.toLowerCase().includes(query) ||
      seg.speaker?.toLowerCase().includes(query)
    );
  }, [segments, searchQuery]);

  // Link contacts mutation
  const linkContactsMutation = useMutation({
    mutationFn: (contactIds) => apiClient.put(`/api/plaud/recordings/${recording.id}/link`, { contact_ids: contactIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings'] });
      queryClient.invalidateQueries({ queryKey: ['plaud-recording', recording.id] });
      setShowLinkContacts(false);
      onContactsLinked?.();
    },
  });

  // Scroll to segment by index
  const scrollToSegment = useCallback((index) => {
    const segmentEl = document.getElementById(`segment-${index}`);
    if (segmentEl) {
      segmentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      segmentEl.classList.add('aw-segment-flash');
      setTimeout(() => segmentEl.classList.remove('aw-segment-flash'), 1500);
    }
  }, []);

  // Handle first search result jump
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    // After state update, scroll to first match
    if (query) {
      setTimeout(() => {
        const firstMatch = document.querySelector('.aw-transcript-highlight');
        if (firstMatch) {
          const segment = firstMatch.closest('.aw-transcript-segment');
          segment?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, []);

  // Toggle contact selection
  const toggleContact = (contactId) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Copy full transcript to clipboard
  const copyTranscript = async () => {
    const text = segments.map(seg =>
      `[${fmtTimestamp(seg.start_ms)}] ${seg.speaker}: ${seg.content}`
    ).join('\n\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  // Copy formatted markdown for Google Docs (transcript + summary + notes)
  const copyForGoogleDocs = async () => {
    const lines = [];

    // Title
    lines.push(`# ${recording.name}`);
    lines.push('');
    lines.push(`**Date:** ${fmtDate(recording.recorded_at) || 'Unknown'}`);
    if (recording.duration_formatted) {
      lines.push(`**Duration:** ${recording.duration_formatted}`);
    }
    lines.push('');

    // Summary
    if (recording.summary) {
      lines.push('## Summary');
      lines.push('');
      lines.push(recording.summary);
      lines.push('');
    }

    // Key Topics
    if (topics.length > 0) {
      lines.push('## Key Topics');
      lines.push('');
      topics.forEach(topic => {
        lines.push(`- ${topic}`);
      });
      lines.push('');
    }

    // Action Items
    if (actionItems.length > 0) {
      lines.push('## Action Items');
      lines.push('');
      actionItems.forEach(item => {
        lines.push(`- [ ] ${item}`);
      });
      lines.push('');
    }

    // Full Transcript
    if (segments.length > 0) {
      lines.push('## Transcript');
      lines.push('');
      segments.forEach(seg => {
        const speaker = seg.speaker || 'Speaker';
        lines.push(`**[${fmtTimestamp(seg.start_ms)}] ${speaker}:**`);
        lines.push(seg.content);
        lines.push('');
      });
    }

    const markdown = lines.join('\n');

    try {
      await navigator.clipboard.writeText(markdown);
      setCopiedForDocs(true);
      setTimeout(() => setCopiedForDocs(false), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  return (
    <div className="aw-modal-overlay" onClick={onClose}>
      <div className="aw-modal aw-plaud-transcript-modal" onClick={e => e.stopPropagation()}>
        <header className="aw-modal-header">
          <div className="aw-modal-header-content">
            <h2><FileText size={18} /> {recording.name}</h2>
            <span className="aw-modal-subtitle">
              {fmtDate(recording.recorded_at)}
              {recording.duration_formatted && ` · ${recording.duration_formatted}`}
            </span>
          </div>
          <div className="aw-modal-header-actions">
            {contacts.length > 0 && (
              <button
                className={`aw-modal-btn ${showLinkContacts ? 'active' : ''}`}
                onClick={() => setShowLinkContacts(!showLinkContacts)}
                title="Link contacts to this recording"
              >
                <Users size={16} />
              </button>
            )}
            <button
              className="aw-modal-btn"
              onClick={copyTranscript}
              title="Copy transcript only"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <button
              className="aw-modal-btn"
              onClick={copyForGoogleDocs}
              title="Copy for Google Docs (transcript + summary + notes)"
            >
              {copiedForDocs ? <Check size={16} /> : <FileDown size={16} />}
            </button>
            <button className="aw-modal-close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </header>

        {/* Link Contacts Panel */}
        {showLinkContacts && (
          <div className="aw-transcript-link-contacts">
            <div className="aw-link-contacts-header">
              <Users size={14} />
              <span>Link Contacts to This Recording</span>
            </div>
            <div className="aw-link-contacts-list">
              {contacts.map((contact) => {
                const isSelected = selectedContacts.includes(contact.id);
                return (
                  <label key={contact.id} className={`aw-link-contact-item ${isSelected ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleContact(contact.id)}
                    />
                    <span className="aw-contact-name">
                      {contact.first_name} {contact.last_name || ''}
                    </span>
                    {contact.title && (
                      <span className="aw-contact-title">{contact.title}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="aw-link-contacts-actions">
              <button
                className="aw-btn aw-btn-secondary"
                onClick={() => setShowLinkContacts(false)}
              >
                Cancel
              </button>
              <button
                className="aw-btn aw-btn-primary"
                onClick={() => linkContactsMutation.mutate(selectedContacts)}
                disabled={linkContactsMutation.isPending}
              >
                {linkContactsMutation.isPending ? <Loader2 size={14} className="aw-spin" /> : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Topics & Action Items */}
        {(topics.length > 0 || actionItems.length > 0) && (
          <div className="aw-transcript-topics">
            <button
              className="aw-transcript-topics-toggle"
              onClick={() => setShowTopics(!showTopics)}
            >
              {showTopics ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <span>Key Topics & Action Items</span>
              <span className="aw-topics-count">{topics.length + actionItems.length}</span>
            </button>
            {showTopics && (
              <div className="aw-transcript-topics-content">
                {topics.length > 0 && (
                  <div className="aw-topics-section">
                    <h4><Tag size={12} /> Topics</h4>
                    <ul>
                      {topics.map((topic, i) => (
                        <li key={i}>{topic}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {actionItems.length > 0 && (
                  <div className="aw-topics-section">
                    <h4><CheckSquare size={12} /> Action Items</h4>
                    <ul>
                      {actionItems.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary section */}
        {recording.summary && (
          <div className="aw-transcript-summary">
            <h3>Summary</h3>
            <p>{recording.summary}</p>
          </div>
        )}

        {/* Search bar */}
        <div className="aw-transcript-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search transcript..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchQuery && (
            <span className="aw-transcript-search-count">
              {filteredSegments.length} match{filteredSegments.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        <div className="aw-modal-body aw-transcript-body">
          {isLoading && (
            <div className="aw-transcript-loading">
              <Loader2 size={24} className="aw-spin" />
              <p>Loading transcript...</p>
            </div>
          )}

          {error && (
            <div className="aw-transcript-error">
              <AlertCircle size={18} />
              <p>{error.message || 'Failed to load transcript'}</p>
            </div>
          )}

          {!isLoading && !error && segments.length === 0 && (
            <div className="aw-transcript-empty">
              <FileText size={32} strokeWidth={1} />
              <p>No transcript available for this recording.</p>
            </div>
          )}

          {!isLoading && !error && filteredSegments.length > 0 && (
            <div className="aw-transcript-segments" ref={segmentsRef}>
              {filteredSegments.map((seg, i) => {
                const speaker = seg.speaker || 'Speaker';
                const speakerColor = speakerColorMap.get(speaker) || SPEAKER_COLORS[0];
                const originalIndex = segments.indexOf(seg);

                return (
                  <div
                    key={i}
                    id={`segment-${originalIndex}`}
                    className="aw-transcript-segment"
                  >
                    <div className="aw-segment-meta">
                      <button
                        className="aw-segment-time"
                        onClick={() => scrollToSegment(originalIndex)}
                        title="Jump to this timestamp"
                      >
                        <Clock size={11} />
                        {fmtTimestamp(seg.start_ms)}
                      </button>
                      <span
                        className="aw-segment-speaker"
                        style={{ color: speakerColor }}
                      >
                        <User size={11} />
                        {speaker}
                      </span>
                    </div>
                    <p className="aw-segment-content">
                      {highlightMatch(seg.content, searchQuery)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && !error && searchQuery && filteredSegments.length === 0 && (
            <div className="aw-transcript-no-results">
              <p>No matches found for "{searchQuery}"</p>
            </div>
          )}
        </div>

        <footer className="aw-modal-footer">
          <span className="aw-transcript-stats">
            {segments.length} segments
            {recording.chunks_count > 0 && ` · ${recording.chunks_count} searchable chunks`}
          </span>
          <button className="aw-btn aw-btn-secondary" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

export default PlaudTranscriptModal;
